import { useState, useEffect } from 'react';
import {
  ClockIcon,
  ArrowPathIcon,
  CheckIcon,
  XMarkIcon,
  CalendarIcon,
  AdjustmentsHorizontalIcon,
  KeyIcon
} from '@heroicons/react/24/outline';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' }
];

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

const FREQUENCIES = [
  { value: 'manual', label: 'Manual Only', description: 'Sync only when triggered manually' },
  { value: 'hourly', label: 'Hourly', description: 'Sync every hour' },
  { value: 'daily', label: 'Daily', description: 'Sync once per day' },
  { value: 'weekly', label: 'Weekly', description: 'Sync once per week' },
  { value: 'monthly', label: 'Monthly', description: 'Sync once per month' },
  { value: 'yearly', label: 'Yearly', description: 'Sync once per year' }
];

// REST API Incremental sync strategy options
const INCREMENTAL_STRATEGIES = [
  {
    id: 'query',
    name: 'Query Parameter',
    description: 'Add filter as URL query parameter',
    example: '?updated_since=2024-01-01T00:00:00Z'
  },
  {
    id: 'path',
    name: 'Path Parameter',
    description: 'Replace placeholder in URL path',
    example: '/api/events/2024-01-01/latest'
  },
  {
    id: 'header',
    name: 'Header-based',
    description: 'Send value in HTTP header',
    example: 'If-Modified-Since: Wed, 21 Oct 2024 07:28:00 GMT'
  },
  {
    id: 'body',
    name: 'Body Parameter',
    description: 'Include filter in request body (POST/PUT)',
    example: '{ "filter": { "updatedAfter": "2024-01-01" } }'
  },
  {
    id: 'cursor',
    name: 'Cursor + State',
    description: 'Track incremental value with cursor-based pagination',
    example: 'Requires cursor pagination • Tracks timestamp for resume capability',
    requiresCursor: true
  }
];

const DATE_FORMATS = [
  { id: 'iso8601', name: 'ISO 8601', example: '2024-01-01T00:00:00Z', description: 'Standard ISO format with time' },
  { id: 'iso8601_date', name: 'ISO 8601 Date', example: '2024-01-01', description: 'Date only (YYYY-MM-DD)' },
  { id: 'unix', name: 'Unix Timestamp', example: '1704067200', description: 'Seconds since epoch' },
  { id: 'unix_ms', name: 'Unix Milliseconds', example: '1704067200000', description: 'Milliseconds since epoch' },
  { id: 'rfc2822', name: 'RFC 2822', example: 'Wed, 21 Oct 2024 07:28:00 GMT', description: 'HTTP date format' },
  { id: 'custom', name: 'Custom Format', example: 'Use column type formatting', description: 'Based on column type setting' }
];

// Plan-specific frequency limits
const PLAN_FREQUENCIES = {
  starter: ['manual', 'yearly', 'monthly', 'weekly'],
  basic: ['manual', 'yearly', 'monthly', 'weekly', 'daily'],
  pro: ['manual', 'yearly', 'monthly', 'weekly', 'daily', 'hourly'],
  business: ['manual', 'yearly', 'monthly', 'weekly', 'daily', 'hourly'],
  enterprise: ['manual', 'yearly', 'monthly', 'weekly', 'daily', 'hourly']
};

// Get available frequencies for a given plan
const getAvailableFrequencies = (plan) => {
  const allowedFreqs = PLAN_FREQUENCIES[plan] || PLAN_FREQUENCIES.starter;
  return FREQUENCIES.filter(freq => allowedFreqs.includes(freq.value));
};

export default function SyncScheduleEditor({ 
  schedule, 
  companyTimezone = 'UTC', 
  companyPlan = 'starter', 
  columns = [], // Available columns for incremental sync tracking
  incrementalConfig = null, // Current incremental config from data source
  deduplicationConfig = null, // Current deduplication config from data source
  dataSourceType = null, // Type of data source (postgresql, mysql, mongodb, etc.)
  onSave, 
  onCancel, 
  isSaving 
}) {
  const [localSchedule, setLocalSchedule] = useState({
    enabled: schedule?.enabled || false,
    frequency: schedule?.frequency || 'manual',
    importMode: schedule?.importMode || 'full',
    minute: schedule?.minute || 0,
    hour: schedule?.hour || 0,
    dayOfWeek: schedule?.dayOfWeek || 0,
    dayOfMonth: schedule?.dayOfMonth || 1,
    month: schedule?.month || 1
    // Note: timezone is set automatically from company settings on the backend
  });
  
  // Incremental sync configuration
  const [localIncrementalConfig, setLocalIncrementalConfig] = useState({
    enabled: incrementalConfig?.enabled || false,
    column: incrementalConfig?.column || '',
    columnType: incrementalConfig?.columnType || 'auto',
    // REST API strategy fields
    strategy: incrementalConfig?.strategy || 'query',
    strategyConfig: incrementalConfig?.strategyConfig || {
      paramName: '',
      template: '',
      placeholder: '',
      headerName: 'If-Modified-Since',
      format: 'iso8601',
      paramPath: ''
    }
  });
  
  // Deduplication configuration
  const [localDeduplicationConfig, setLocalDeduplicationConfig] = useState({
    enabled: deduplicationConfig?.enabled ?? true, // Default to enabled
    keyColumns: deduplicationConfig?.keyColumns || []
  });
  
  // Check if this is a database source that supports incremental sync
  const isNoSQL = dataSourceType === 'mongodb';
  const isDatabase = ['postgresql', 'mysql', 'mongodb'].includes(dataSourceType);
  
  // Column type options for incremental sync (must be after isNoSQL is defined)
  const getColumnTypeOptions = () => [
    { value: 'auto', label: 'Auto-detect', description: 'Automatically detect date formats' },
    { value: 'date', label: 'Date', description: 'Date values (e.g., 2026-01-21)' },
    { value: 'datetime', label: 'Date/Time', description: 'Timestamp values (e.g., 2026-01-21T10:30:00Z)' },
    { value: 'number', label: 'Number', description: 'Numeric values (e.g., auto-incrementing IDs)' },
    { value: 'string', label: 'String', description: 'Text values (compared alphabetically)' },
    ...(isNoSQL ? [{ value: 'objectid', label: 'ObjectId', description: 'MongoDB ObjectId (_id field)' }] : [])
  ];
  // Also support API sources for incremental sync
  const supportsIncrementalConfig = isDatabase || dataSourceType === 'api';
  // Check if data source type supports incremental sync mode
  // Google Sheets, CSV, and Excel don't support incremental - they always do full sync
  const supportsIncrementalSync = isDatabase || dataSourceType === 'api';
  // All database and API sources support deduplication
  const supportsDeduplication = isDatabase || dataSourceType === 'api';

  // Format timezone for display (e.g., "America/New_York" -> "America/New York")
  const formatTimezone = (tz) => {
    if (!tz) return 'UTC';
    return tz.replace(/_/g, ' ');
  };

  useEffect(() => {
    // Auto-enable when frequency changes from manual
    if (localSchedule.frequency !== 'manual' && !localSchedule.enabled) {
      setLocalSchedule(prev => ({ ...prev, enabled: true }));
    }
    // Auto-disable when frequency is manual
    if (localSchedule.frequency === 'manual' && localSchedule.enabled) {
      setLocalSchedule(prev => ({ ...prev, enabled: false }));
    }
  }, [localSchedule.frequency, localSchedule.enabled]);

  const updateField = (field, value) => {
    setLocalSchedule(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    // Include incremental config and deduplication config when saving
    const incrementalData = localSchedule.importMode === 'incremental' && localIncrementalConfig.column
      ? {
          enabled: true,
          column: localIncrementalConfig.column,
          columnType: localIncrementalConfig.columnType || 'auto',
          // Include REST API strategy fields
          ...(dataSourceType === 'api' && {
            strategy: localIncrementalConfig.strategy,
            strategyConfig: {
              ...(localIncrementalConfig.strategy === 'query' && {
                paramName: localIncrementalConfig.strategyConfig?.paramName
              }),
              ...(localIncrementalConfig.strategy === 'path' && {
                template: localIncrementalConfig.strategyConfig?.template,
                placeholder: localIncrementalConfig.strategyConfig?.placeholder
              }),
              ...(localIncrementalConfig.strategy === 'header' && {
                headerName: localIncrementalConfig.strategyConfig?.headerName,
                format: localIncrementalConfig.strategyConfig?.format
              }),
              ...(localIncrementalConfig.strategy === 'body' && {
                paramPath: localIncrementalConfig.strategyConfig?.paramPath
              })
            },
            // Legacy: include paramName at root for backward compatibility
            ...(localIncrementalConfig.strategy === 'query' && {
              paramName: localIncrementalConfig.strategyConfig?.paramName
            })
          })
        }
      : { enabled: false };

    const saveData = {
      ...localSchedule,
      incrementalConfig: incrementalData,
      deduplicationConfig: {
        enabled: localDeduplicationConfig.enabled,
        keyColumns: localDeduplicationConfig.keyColumns
      }
    };
    onSave(saveData);
  };
  
  // Toggle a column in the deduplication key columns
  const toggleDeduplicationKey = (columnName) => {
    setLocalDeduplicationConfig(prev => {
      const isSelected = prev.keyColumns.includes(columnName);
      return {
        ...prev,
        keyColumns: isSelected
          ? prev.keyColumns.filter(c => c !== columnName)
          : [...prev.keyColumns, columnName]
      };
    });
  };

  // Generate hours options (00-23)
  const hours = Array.from({ length: 24 }, (_, i) => ({
    value: i,
    label: i.toString().padStart(2, '0') + ':00'
  }));

  // Generate minutes options (0, 15, 30, 45)
  const minutes = [0, 15, 30, 45].map(m => ({
    value: m,
    label: m.toString().padStart(2, '0')
  }));

  // Generate days of month (1-31)
  const daysOfMonth = Array.from({ length: 31 }, (_, i) => ({
    value: i + 1,
    label: (i + 1).toString()
  }));

  // Get available frequencies for the current plan
  const availableFrequencies = getAvailableFrequencies(companyPlan);
  
  // Check if current frequency is available
  const isCurrentFrequencyAvailable = availableFrequencies.some(f => f.value === localSchedule.frequency);

  const getScheduleDescription = () => {
    if (localSchedule.frequency === 'manual') {
      return 'Sync will only run when triggered manually.';
    }

    const timeStr = `${localSchedule.hour.toString().padStart(2, '0')}:${localSchedule.minute.toString().padStart(2, '0')}`;
    const tz = formatTimezone(companyTimezone);
    
    switch (localSchedule.frequency) {
      case 'hourly':
        return `Sync will run every hour at minute ${localSchedule.minute.toString().padStart(2, '0')} (${tz} timezone).`;
      case 'daily':
        return `Sync will run every day at ${timeStr} (${tz} timezone).`;
      case 'weekly':
        const dayName = DAYS_OF_WEEK.find(d => d.value === localSchedule.dayOfWeek)?.label;
        return `Sync will run every ${dayName} at ${timeStr} (${tz} timezone).`;
      case 'monthly':
        return `Sync will run on day ${localSchedule.dayOfMonth} of every month at ${timeStr} (${tz} timezone).`;
      case 'yearly':
        const monthName = MONTHS.find(m => m.value === localSchedule.month)?.label;
        return `Sync will run on ${monthName} ${localSchedule.dayOfMonth} at ${timeStr} every year (${tz} timezone).`;
      default:
        return '';
    }
  };
  
  const getPlanName = (plan) => {
    const names = {
      starter: 'Starter',
      basic: 'Basic',
      pro: 'Pro',
      business: 'Business',
      enterprise: 'Enterprise'
    };
    return names[plan] || 'Starter';
  };

  return (
    <div className="space-y-6">
      {/* Sync Mode */}
      <div>
        <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
          Sync Mode
        </label>
        <div className={`grid gap-3 ${supportsIncrementalSync ? 'grid-cols-2' : 'grid-cols-1'}`}>
          <button
            type="button"
            onClick={() => updateField('importMode', 'full')}
            className={`p-4 rounded-lg border-2 text-left transition-colors ${
              localSchedule.importMode === 'full'
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'border-surface-200 dark:border-surface-700 hover:border-surface-300'
            }`}
          >
            <div className="font-medium text-surface-900 dark:text-white">Full Refresh</div>
            <div className="text-sm text-surface-500 mt-1">
              Replace all existing data with new data
            </div>
          </button>
          {supportsIncrementalSync && (
            <button
              type="button"
              onClick={() => updateField('importMode', 'incremental')}
              className={`p-4 rounded-lg border-2 text-left transition-colors ${
                localSchedule.importMode === 'incremental'
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-surface-200 dark:border-surface-700 hover:border-surface-300'
              }`}
            >
              <div className="font-medium text-surface-900 dark:text-white">Incremental</div>
              <div className="text-sm text-surface-500 mt-1">
                Only add new records since last sync
              </div>
            </button>
          )}
        </div>
        {!supportsIncrementalSync && (
          <p className="text-xs text-surface-500 mt-2">
            Incremental sync is not available for this data source type. All syncs will refresh the full dataset.
          </p>
        )}
      </div>

      {/* Incremental Sync Configuration - shown when importMode is incremental */}
      {localSchedule.importMode === 'incremental' && supportsIncrementalConfig && (
        <div className="p-4 bg-surface-50 dark:bg-surface-800/50 rounded-lg space-y-4">
          <div className="flex items-center gap-2">
            <AdjustmentsHorizontalIcon className="w-5 h-5 text-surface-400" />
            <h4 className="font-medium text-surface-900 dark:text-white">
              Incremental Sync Settings
            </h4>
          </div>

          <p className="text-sm text-surface-500">
            {dataSourceType === 'api'
              ? 'Configure how your REST API filters for new or updated records.'
              : 'Configure which column to track for identifying new or updated records.'
            }
          </p>

          {/* REST API Strategy Selection */}
          {dataSourceType === 'api' && (
            <div>
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-3">
                Sync Strategy
              </label>
              <div className="grid grid-cols-1 gap-2">
                {INCREMENTAL_STRATEGIES.map((strategy) => (
                  <button
                    key={strategy.id}
                    type="button"
                    onClick={() => {
                      setLocalIncrementalConfig({
                        ...localIncrementalConfig,
                        strategy: strategy.id
                      });
                    }}
                    className={`${
                      localIncrementalConfig.strategy === strategy.id
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-800'
                    } relative flex items-start p-3 border rounded-lg hover:border-primary-400 dark:hover:border-primary-600 transition-colors text-left`}
                  >
                    <div className="flex items-center h-5">
                      <input
                        type="radio"
                        checked={localIncrementalConfig.strategy === strategy.id}
                        onChange={() => {}}
                        className="h-4 w-4 text-primary-600 border-surface-300"
                      />
                    </div>
                    <div className="ml-3 flex-1">
                      <div className="text-sm font-medium text-surface-900 dark:text-white">
                        {strategy.name}
                      </div>
                      <div className="text-xs text-surface-500 mt-0.5">
                        {strategy.description}
                      </div>
                      <div className="text-xs text-surface-400 mt-1 font-mono">
                        {strategy.example}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Common Fields: Tracking Column */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                Tracking Column
              </label>
              {columns.length > 0 ? (
                <select
                  value={localIncrementalConfig.column}
                  onChange={(e) => setLocalIncrementalConfig({ ...localIncrementalConfig, column: e.target.value })}
                  className="input"
                >
                  <option value="">Select a column...</option>
                  {columns.map(col => (
                    <option key={col.name} value={col.name}>
                      {col.name} ({col.type})
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={localIncrementalConfig.column}
                  onChange={(e) => setLocalIncrementalConfig({ ...localIncrementalConfig, column: e.target.value })}
                  className="input font-mono text-sm"
                  placeholder={dataSourceType === 'api' ? 'updated_at, id, etc.' : (isNoSQL ? 'updatedAt' : 'updated_at')}
                />
              )}
              <p className="text-xs text-surface-500 mt-1">
                {dataSourceType === 'api'
                  ? 'The field in the API response that tracks when records change'
                  : (isNoSQL
                    ? 'The field to track for changes (e.g., updatedAt, _id, createdAt)'
                    : 'The column to track for changes (e.g., updated_at, id, created_at)'
                  )
                }
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                Column Data Type
              </label>
              <select
                value={localIncrementalConfig.columnType}
                onChange={(e) => setLocalIncrementalConfig({ ...localIncrementalConfig, columnType: e.target.value })}
                className="input"
              >
                {getColumnTypeOptions().map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-surface-500 mt-1">
                {getColumnTypeOptions().find(o => o.value === localIncrementalConfig.columnType)?.description || 'Select the data type of the tracking column'}
              </p>
            </div>
          </div>

          {/* REST API Strategy-Specific Fields */}
          {dataSourceType === 'api' && (
            <>
              {localIncrementalConfig.strategy === 'query' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                      API Parameter Name
                    </label>
                    <input
                      type="text"
                      value={localIncrementalConfig.strategyConfig?.paramName || ''}
                      onChange={(e) =>
                        setLocalIncrementalConfig({
                          ...localIncrementalConfig,
                          strategyConfig: {
                            ...localIncrementalConfig.strategyConfig,
                            paramName: e.target.value
                          }
                        })
                      }
                      className="input font-mono text-sm"
                      placeholder="updated_since, modified_after, etc."
                    />
                    <p className="text-xs text-surface-500 mt-1">
                      The query parameter name that your API uses for filtering
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                      Date/Time Format
                    </label>
                    <select
                      value={localIncrementalConfig.strategyConfig?.format || 'iso8601'}
                      onChange={(e) =>
                        setLocalIncrementalConfig({
                          ...localIncrementalConfig,
                          strategyConfig: {
                            ...localIncrementalConfig.strategyConfig,
                            format: e.target.value
                          }
                        })
                      }
                      className="input"
                    >
                      {DATE_FORMATS.map((format) => (
                        <option key={format.id} value={format.id}>
                          {format.name} - {format.example}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-surface-500 mt-1">
                      {DATE_FORMATS.find(f => f.id === (localIncrementalConfig.strategyConfig?.format || 'iso8601'))?.description}
                    </p>
                  </div>
                </div>
              )}

              {localIncrementalConfig.strategy === 'path' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                      URL Template
                    </label>
                    <input
                      type="text"
                      value={localIncrementalConfig.strategyConfig?.template || ''}
                      onChange={(e) =>
                        setLocalIncrementalConfig({
                          ...localIncrementalConfig,
                          strategyConfig: {
                            ...localIncrementalConfig.strategyConfig,
                            template: e.target.value
                          }
                        })
                      }
                      className="input font-mono text-sm"
                      placeholder="/api/events/{lastDate}/latest"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                      Placeholder Name
                    </label>
                    <input
                      type="text"
                      value={localIncrementalConfig.strategyConfig?.placeholder || ''}
                      onChange={(e) =>
                        setLocalIncrementalConfig({
                          ...localIncrementalConfig,
                          strategyConfig: {
                            ...localIncrementalConfig.strategyConfig,
                            placeholder: e.target.value
                          }
                        })
                      }
                      className="input"
                      placeholder="lastDate"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                      Date/Time Format
                    </label>
                    <select
                      value={localIncrementalConfig.strategyConfig?.pathFormat || 'iso8601'}
                      onChange={(e) =>
                        setLocalIncrementalConfig({
                          ...localIncrementalConfig,
                          strategyConfig: {
                            ...localIncrementalConfig.strategyConfig,
                            pathFormat: e.target.value
                          }
                        })
                      }
                      className="input"
                    >
                      {DATE_FORMATS.map((format) => (
                        <option key={format.id} value={format.id}>
                          {format.name} - {format.example}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-surface-500 mt-1">
                      {DATE_FORMATS.find(f => f.id === (localIncrementalConfig.strategyConfig?.pathFormat || 'iso8601'))?.description}
                    </p>
                  </div>
                </div>
              )}

              {localIncrementalConfig.strategy === 'header' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                      Header Name
                    </label>
                    <input
                      type="text"
                      value={localIncrementalConfig.strategyConfig?.headerName || ''}
                      onChange={(e) =>
                        setLocalIncrementalConfig({
                          ...localIncrementalConfig,
                          strategyConfig: {
                            ...localIncrementalConfig.strategyConfig,
                            headerName: e.target.value
                          }
                        })
                      }
                      className="input"
                      placeholder="If-Modified-Since"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                      Date/Time Format
                    </label>
                    <select
                      value={localIncrementalConfig.strategyConfig?.headerFormat || 'iso8601'}
                      onChange={(e) =>
                        setLocalIncrementalConfig({
                          ...localIncrementalConfig,
                          strategyConfig: {
                            ...localIncrementalConfig.strategyConfig,
                            headerFormat: e.target.value
                          }
                        })
                      }
                      className="input"
                    >
                      {DATE_FORMATS.map((format) => (
                        <option key={format.id} value={format.id}>
                          {format.name} - {format.example}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-surface-500 mt-1">
                      {DATE_FORMATS.find(f => f.id === (localIncrementalConfig.strategyConfig?.headerFormat || 'iso8601'))?.description}
                    </p>
                  </div>
                </div>
              )}

              {localIncrementalConfig.strategy === 'body' && (
                <div>
                  <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                    Parameter Path (dot notation)
                  </label>
                  <input
                    type="text"
                    value={localIncrementalConfig.strategyConfig?.paramPath || ''}
                    onChange={(e) =>
                      setLocalIncrementalConfig({
                        ...localIncrementalConfig,
                        strategyConfig: {
                          ...localIncrementalConfig.strategyConfig,
                          paramPath: e.target.value
                        }
                      })
                    }
                    className="input font-mono text-sm"
                    placeholder="filter.updatedAfter"
                  />
                  <p className="text-xs text-surface-500 mt-1">
                    Nested path in request body where the filter value will be placed
                  </p>
                </div>
              )}

              {localIncrementalConfig.strategy === 'cursor' && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-sm text-blue-700 dark:text-blue-400">
                    <strong>Cursor + State Strategy</strong>
                    <br />
                    <span className="mt-1 block text-xs">
                      This strategy tracks the incremental value alongside cursor pagination.
                      The cursor handles fetching, while the tracked value enables resume capability.
                    </span>
                  </p>
                </div>
              )}
            </>
          )}

          {localIncrementalConfig.column && dataSourceType !== 'api' && (
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-blue-700 dark:text-blue-400">
                <strong>How it works:</strong> After the first full sync, subsequent syncs will only fetch records where
                <code className="mx-1 px-1 bg-blue-100 dark:bg-blue-800 rounded">{localIncrementalConfig.column}</code>
                is greater than the last synced value.
              </p>
            </div>
          )}

          {!localIncrementalConfig.column && (
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <p className="text-sm text-amber-700 dark:text-amber-400">
                <strong>Note:</strong> Please select a tracking column for incremental sync to work properly.
                Without a tracking column, the sync will behave like a full refresh.
              </p>
            </div>
          )}

          {dataSourceType !== 'api' && (
            <div className="text-xs text-surface-400 space-y-1">
              <p><strong>Recommended columns:</strong></p>
              <ul className="list-disc list-inside space-y-0.5 ml-2">
                <li><code className="bg-surface-100 dark:bg-surface-800 px-1 rounded">{isNoSQL ? 'updatedAt' : 'updated_at'}</code> - Best for tracking modified records</li>
                <li><code className="bg-surface-100 dark:bg-surface-800 px-1 rounded">{isNoSQL ? 'createdAt' : 'created_at'}</code> - For append-only data</li>
                <li><code className="bg-surface-100 dark:bg-surface-800 px-1 rounded">{isNoSQL ? '_id' : 'id'}</code> - For auto-incrementing IDs</li>
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Deduplication Settings - shown for incremental sync with supported data sources */}
      {localSchedule.importMode === 'incremental' && supportsDeduplication && (
        <div className="p-4 bg-surface-50 dark:bg-surface-800/50 rounded-lg space-y-4">
          <div className="flex items-center gap-2">
            <KeyIcon className="w-5 h-5 text-surface-400" />
            <h4 className="font-medium text-surface-900 dark:text-white">
              Deduplication Settings
            </h4>
          </div>
          
          <p className="text-sm text-surface-500">
            Configure which column(s) uniquely identify each record. This prevents duplicate data during incremental syncs.
          </p>
          
          <div className="flex items-center gap-3">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={localDeduplicationConfig.enabled}
                onChange={(e) => setLocalDeduplicationConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-surface-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-surface-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-surface-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-surface-600 peer-checked:bg-primary-600"></div>
            </label>
            <span className="text-sm font-medium text-surface-700 dark:text-surface-300">
              Enable deduplication (MERGE instead of APPEND)
            </span>
          </div>
          
          {localDeduplicationConfig.enabled && (
            <div>
              <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
                Unique Key Column(s)
              </label>
              {columns.length > 0 ? (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2 p-3 border border-surface-200 dark:border-surface-700 rounded-lg min-h-[42px]">
                    {localDeduplicationConfig.keyColumns.length === 0 ? (
                      <span className="text-surface-400 text-sm">Click columns below to select unique keys...</span>
                    ) : (
                      localDeduplicationConfig.keyColumns.map(col => (
                        <span 
                          key={col}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-md text-sm"
                        >
                          {col}
                          <button
                            type="button"
                            onClick={() => toggleDeduplicationKey(col)}
                            className="hover:text-primary-900 dark:hover:text-primary-100"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                        </span>
                      ))
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                    {columns.map(col => {
                      const isSelected = localDeduplicationConfig.keyColumns.includes(col.name);
                      return (
                        <button
                          key={col.name}
                          type="button"
                          onClick={() => toggleDeduplicationKey(col.name)}
                          className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                            isSelected
                              ? 'bg-primary-500 text-white border-primary-500'
                              : 'bg-surface-100 dark:bg-surface-800 text-surface-700 dark:text-surface-300 border-surface-300 dark:border-surface-600 hover:border-primary-400'
                          }`}
                        >
                          {col.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <input
                  type="text"
                  value={localDeduplicationConfig.keyColumns.join(', ')}
                  onChange={(e) => setLocalDeduplicationConfig(prev => ({
                    ...prev,
                    keyColumns: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                  }))}
                  className="input font-mono text-sm"
                  placeholder={isNoSQL ? '_id' : 'id'}
                />
              )}
              <p className="text-xs text-surface-500 mt-1">
                Select the column(s) that uniquely identify each record (e.g., {isNoSQL ? '_id' : 'id'}, email, etc.)
              </p>
            </div>
          )}
          
          {localDeduplicationConfig.enabled && localDeduplicationConfig.keyColumns.length > 0 && (
            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <p className="text-sm text-green-700 dark:text-green-400">
                <strong>How it works:</strong> Records matching on 
                <code className="mx-1 px-1 bg-green-100 dark:bg-green-800 rounded">{localDeduplicationConfig.keyColumns.join(', ')}</code> 
                will be <strong>updated</strong>. New records will be <strong>inserted</strong>.
              </p>
            </div>
          )}
          
          {localDeduplicationConfig.enabled && localDeduplicationConfig.keyColumns.length === 0 && (
            <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
              <p className="text-sm text-amber-700 dark:text-amber-400">
                <strong>Warning:</strong> Without key columns, deduplication cannot work. 
                All records will be appended, potentially creating duplicates.
              </p>
            </div>
          )}
          
          <div className="text-xs text-surface-400 space-y-1">
            <p><strong>Recommended key columns:</strong></p>
            <ul className="list-disc list-inside space-y-0.5 ml-2">
              <li><code className="bg-surface-100 dark:bg-surface-800 px-1 rounded">{isNoSQL ? '_id' : 'id'}</code> - Primary key / unique identifier</li>
              <li>Composite keys like <code className="bg-surface-100 dark:bg-surface-800 px-1 rounded">{isNoSQL ? 'userId + orderId' : 'user_id + order_id'}</code> for junction tables</li>
            </ul>
          </div>
        </div>
      )}

      {/* Frequency */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-surface-700 dark:text-surface-300">
            Sync Frequency
          </label>
          <span className="text-xs text-surface-500">
            {getPlanName(companyPlan)} Plan
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {availableFrequencies.map(freq => (
            <button
              key={freq.value}
              type="button"
              onClick={() => updateField('frequency', freq.value)}
              className={`p-3 rounded-lg border-2 text-left transition-colors ${
                localSchedule.frequency === freq.value
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-surface-200 dark:border-surface-700 hover:border-surface-300'
              }`}
            >
              <div className="font-medium text-surface-900 dark:text-white text-sm">{freq.label}</div>
            </button>
          ))}
        </div>
        {!isCurrentFrequencyAvailable && localSchedule.frequency !== 'manual' && (
          <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <p className="text-sm text-amber-700 dark:text-amber-300">
              ⚠ The current frequency "{localSchedule.frequency}" is not available on your {getPlanName(companyPlan)} plan. 
              Please select an available frequency or upgrade your plan.
            </p>
          </div>
        )}
        <p className="text-xs text-surface-500 mt-2">
          {companyPlan === 'starter' && 'Upgrade to Basic for Daily sync, or Pro/Business/Enterprise for Hourly sync.'}
          {companyPlan === 'basic' && 'Upgrade to Pro, Business, or Enterprise for Hourly sync.'}
          {['pro', 'business', 'enterprise'].includes(companyPlan) && 'All sync frequencies available on your plan.'}
        </p>
      </div>

      {/* Time Settings */}
      {localSchedule.frequency !== 'manual' && (
        <div className="space-y-4 p-4 bg-surface-50 dark:bg-surface-800/50 rounded-lg">
          <h4 className="font-medium text-surface-900 dark:text-white flex items-center gap-2">
            <ClockIcon className="w-5 h-5" />
            Schedule Settings
          </h4>

          {/* Yearly: Month selection */}
          {localSchedule.frequency === 'yearly' && (
            <div>
              <label className="block text-sm text-surface-600 dark:text-surface-400 mb-1">Month</label>
              <select
                value={localSchedule.month}
                onChange={(e) => updateField('month', parseInt(e.target.value))}
                className="input"
              >
                {MONTHS.map(month => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Monthly/Yearly: Day of month selection */}
          {(localSchedule.frequency === 'monthly' || localSchedule.frequency === 'yearly') && (
            <div>
              <label className="block text-sm text-surface-600 dark:text-surface-400 mb-1">Day of Month</label>
              <select
                value={localSchedule.dayOfMonth}
                onChange={(e) => updateField('dayOfMonth', parseInt(e.target.value))}
                className="input"
              >
                {daysOfMonth.map(day => (
                  <option key={day.value} value={day.value}>{day.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Weekly: Day of week selection */}
          {localSchedule.frequency === 'weekly' && (
            <div>
              <label className="block text-sm text-surface-600 dark:text-surface-400 mb-1">Day of Week</label>
              <select
                value={localSchedule.dayOfWeek}
                onChange={(e) => updateField('dayOfWeek', parseInt(e.target.value))}
                className="input"
              >
                {DAYS_OF_WEEK.map(day => (
                  <option key={day.value} value={day.value}>{day.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Time selection (for daily, weekly, monthly, yearly) */}
          {['daily', 'weekly', 'monthly', 'yearly'].includes(localSchedule.frequency) && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-surface-600 dark:text-surface-400 mb-1">Hour</label>
                <select
                  value={localSchedule.hour}
                  onChange={(e) => updateField('hour', parseInt(e.target.value))}
                  className="input"
                >
                  {hours.map(h => (
                    <option key={h.value} value={h.value}>{h.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-surface-600 dark:text-surface-400 mb-1">Minute</label>
                <select
                  value={localSchedule.minute}
                  onChange={(e) => updateField('minute', parseInt(e.target.value))}
                  className="input"
                >
                  {minutes.map(m => (
                    <option key={m.value} value={m.value}>:{m.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Hourly: Minute selection only */}
          {localSchedule.frequency === 'hourly' && (
            <div>
              <label className="block text-sm text-surface-600 dark:text-surface-400 mb-1">
                At minute
              </label>
              <select
                value={localSchedule.minute}
                onChange={(e) => updateField('minute', parseInt(e.target.value))}
                className="input w-32"
              >
                {minutes.map(m => (
                  <option key={m.value} value={m.value}>:{m.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Schedule Description */}
          <div className="p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-800">
            <div className="flex items-start gap-2">
              <CalendarIcon className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-primary-700 dark:text-primary-300">
                {getScheduleDescription()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-surface-200 dark:border-surface-700">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary"
          disabled={isSaving}
        >
          <XMarkIcon className="w-5 h-5 mr-2" />
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="btn-primary"
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <ArrowPathIcon className="w-5 h-5 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <CheckIcon className="w-5 h-5 mr-2" />
              Save Schedule
            </>
          )}
        </button>
      </div>
    </div>
  );
}

